import { forwardRef, useState, type HTMLAttributes } from 'react';
import { cn } from '../../utils/cn';
import { ProjectCard } from './ProjectCard';
import { ProjectModal } from './ProjectModal';
import type { BaseComponentProps } from '../../types';

/**
 * Varianti Portfolio
 */
export type PortfolioVariant = 'grid' | 'carousel' | 'filterable' | 'modal' | 'minimal';

/**
 * Progetto singolo
 */
export interface Project {
  id: string | number;
  title: string;
  description: string;
  image: string;
  category?: string;
  tags?: string[];
  link?: string;
  details?: string;
}

export interface PortfolioProps extends HTMLAttributes<HTMLElement>, BaseComponentProps {
  /**
   * Variante portfolio
   * @default 'grid'
   */
  variant?: PortfolioVariant;
  /**
   * Titolo sezione
   */
  title?: string;
  /**
   * Sottotitolo
   */
  subtitle?: string;
  /**
   * Array progetti
   */
  projects: Project[];
  /**
   * Categorie per filtro
   */
  categories?: string[];
  /**
   * Colonne grid
   */
  columns?: 2 | 3 | 4;
}

/**
 * Portfolio Component
 * 
 * Sezione Portfolio con 5 varianti.
 * Include ProjectCard e ProjectModal.
 * 
 * @example
 * ```tsx
 * <Portfolio
 *   variant="grid"
 *   title="I Nostri Progetti"
 *   projects={[
 *     { id: 1, title: "Progetto 1", image: "...", category: "Web" }
 *   ]}
 * />
 * ```
 */
export const Portfolio = forwardRef<HTMLElement, PortfolioProps>(
  (
    {
      className,
      variant = 'grid',
      title,
      subtitle,
      projects,
      categories = [],
      columns = 3,
      ...props
    },
    ref
  ) => {
    const [selectedProject, setSelectedProject] = useState<Project | null>(null);
    const [activeFilter, setActiveFilter] = useState<string>('all');

    const filteredProjects =
      activeFilter === 'all'
        ? projects
        : projects.filter(p => p.category === activeFilter);

    const gridCols = {
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-2 lg:grid-cols-3',
      4: 'md:grid-cols-2 lg:grid-cols-4',
    };

    // Variant: Grid
    if (variant === 'grid') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-100', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="text-center mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className={cn('grid grid-cols-1 gap-8', gridCols[columns])}>
              {projects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Filterable
    if (variant === 'filterable') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-200', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="text-center mb-12 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            {/* Filtri */}
            <div className="flex justify-center gap-3 mb-12 flex-wrap">
              <button
                onClick={() => setActiveFilter('all')}
                className={cn(
                  'px-6 py-2 rounded-full transition-all font-medium',
                  activeFilter === 'all'
                    ? 'bg-primary text-primary-content'
                    : 'bg-base-100 text-base-content hover:bg-base-300'
                )}
              >
                Tutti
              </button>
              {categories.map((cat) => (
                <button
                  key={cat}
                  onClick={() => setActiveFilter(cat)}
                  className={cn(
                    'px-6 py-2 rounded-full transition-all font-medium',
                    activeFilter === cat
                      ? 'bg-primary text-primary-content'
                      : 'bg-base-100 text-base-content hover:bg-base-300'
                  )}
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Grid progetti */}
            <div className={cn('grid grid-cols-1 gap-8', gridCols[columns])}>
              {filteredProjects.map((project) => (
                <ProjectCard key={project.id} project={project} />
              ))}
            </div>
          </div>
        </section>
      );
    }

    // Variant: Modal
    if (variant === 'modal') {
      return (
        <>
          <section
            ref={ref}
            className={cn('w-full py-20 bg-base-100', className)}
            {...props}
          >
            <div className="container mx-auto px-4">
              {(title || subtitle) && (
                <div className="text-center mb-16 space-y-4">
                  {subtitle && (
                    <p className="text-primary font-semibold uppercase tracking-wide">
                      {subtitle}
                    </p>
                  )}
                  {title && (
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                      {title}
                    </h2>
                  )}
                </div>
              )}

              <div className={cn('grid grid-cols-1 gap-8', gridCols[columns])}>
                {projects.map((project) => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onClick={() => setSelectedProject(project)}
                  />
                ))}
              </div>
            </div>
          </section>

          {selectedProject && (
            <ProjectModal
              project={selectedProject}
              isOpen={!!selectedProject}
              onClose={() => setSelectedProject(null)}
            />
          )}
        </>
      );
    }

    // Variant: Minimal
    if (variant === 'minimal') {
      return (
        <section
          ref={ref}
          className={cn('w-full py-20 bg-base-100', className)}
          {...props}
        >
          <div className="container mx-auto px-4">
            {(title || subtitle) && (
              <div className="mb-16 space-y-4">
                {subtitle && (
                  <p className="text-primary font-semibold uppercase tracking-wide">
                    {subtitle}
                  </p>
                )}
                {title && (
                  <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-base-content">
                    {title}
                  </h2>
                )}
              </div>
            )}

            <div className="max-w-4xl space-y-6">
              {projects.map((project) => (
                <div
                  key={project.id}
                  className="group flex gap-6 p-6 rounded-lg hover:bg-base-200 transition-all cursor-pointer border-l-4 border-transparent hover:border-primary"
                >
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-32 h-32 object-cover rounded-lg flex-shrink-0"
                  />
                  <div>
                    <h3 className="text-2xl font-bold text-base-content group-hover:text-primary transition-colors">
                      {project.title}
                    </h3>
                    <p className="text-base-content/70 mt-2">
                      {project.description}
                    </p>
                    {project.tags && (
                      <div className="flex gap-2 mt-3">
                        {project.tags.map((tag) => (
                          <span
                            key={tag}
                            className="px-3 py-1 text-xs bg-primary/10 text-primary rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      );
    }

    return null;
  }
);

Portfolio.displayName = 'Portfolio';

